<template>
  <div v-element-loader="{ endpoint: 'auth/deliveryMethods' }" class="preferred-delivery-methods">
    <div class="preferred-delivery-methods__container">
      <h3 id="preferred-delivery-methods-title" class="preferred-delivery-methods__title">
        {{ t('preferredDeliveryMethodTitle') }}
      </h3>

      <div class="preferred-delivery-methods__accordion">
        <oie-base-button :text="t('deliveryInformationToggleButtonLabel')" css-classes="button--icon-right button--true-ghost" @click="() => isExpanded = !isExpanded">
          <template v-slot:icon>
            <oie-use-svg svg="chevron-down" :class="{ 'is-expanded': isExpanded }"></oie-use-svg>
          </template>
        </oie-base-button>
        <div v-if="isExpanded" class="accordion__content">
          <div v-for="info in deliveryInformation" :key="info.title" class="accordion__content-item">
            <h4>{{ info.title }}</h4>
            <p>{{ info.description }}</p>
          </div>
        </div>
      </div>

      <fieldset
        ref="radioButtons"
        tabindex="0"
        v-bind="editable ? undefined : {inert: true}"
        aria-labelledby="preferred-delivery-methods-title"
        class="preferred-delivery-methods__radio-buttons">
        <oie-base-radio-checkbox
          v-model="selectedMethod"
          :value="''"
          :label="t('noPreferredDeliveryMethod')"
          name="preffered-delivery-method">
        </oie-base-radio-checkbox>

        <oie-base-radio-checkbox
          v-for="method in deliveryMethods"
          :key="method.id"
          v-model="selectedMethod"
          :value="method.id"
          :label="method.title + method.subTitle"
          :description="getMethodDescription(method)"
          name="preffered-delivery-method">
        </oie-base-radio-checkbox>
      </fieldset>

      <div v-if="editable" class="preferred-delivery-methods__bottom">
        <oie-base-button
          css-classes="button--blue"
          :text="tStatic('genericSave')"
          @click="save"></oie-base-button>
        <oie-base-button class="button--no-background" :text="tStatic('modalCancel')" @click="cancel"></oie-base-button>
      </div>
    </div>

    <oie-base-button
      ref="toggleButton"
      :text="t('changePreferredDeliveryMethod')"
      :disabled="editable"
      css-classes="button--icon-left button--true-ghost preferred-delivery-methods__change-button"
      @click="editDeliveryMethod">
      <template v-slot:icon>
        <oie-use-svg svg="edit-list"></oie-use-svg>
      </template>
    </oie-base-button>
  </div>
</template>

<script>
import httpService from '@/core/http-service'
import { mapActions } from 'vuex'
import _ from 'lodash'

export default {
  data: () => ({
    deliveryMethods: [],
    deliveryInformation: [],
    selectedMethod: '',
    oldMethod: '',
    editable: false,
    isExpanded: false
  }),
  watch: {
    async editable(newVal) {
      if(newVal) {
        await this.$nextTick()
        this.$refs.radioButtons.focus()
      } else {
        this.$refs.toggleButton.$el.focus()
      }
    }
  },
  mounted() {
    this.getPreferredDeliveryMethod()
    this.getDeliveryMethods(this.selectedMethod)
  },
  methods: {
    ...mapActions('checkout', ['clearCheckout']),
    getDeliveryMethods(preserveId) {
      httpService.get('auth/deliveryMethods').then((res) => {
        this.deliveryInformation = res.data.deliveryInformation

        const grouped = []
        const seenKeys = new Set()
        let preservedItem = null

        res.data.deliveryMethods.forEach(method => {
          const key = method.preferredDeliveryGroupingKey

          // Always preserve the one with the specified ID
          if (method.id === preserveId) {
            preservedItem = method
            return // Defer adding it until the end to avoid duplicates
          }

          if (key === null || key === '') {
            grouped.push(method)
          } else if (!seenKeys.has(key)) {
            seenKeys.add(key)
            method.title = method.preferredDeliveryGroupingKey
            method.subTitle = ''
            grouped.push(method)
          }
        })

        // If the preserved item wasn�t already added, add it now
        if (preservedItem && !grouped.find(m => m.id === preservedItem.id)) {
          grouped.push(preservedItem)
        }

        res.data.deliveryMethods = grouped

        console.log(res.data.deliveryMethods)

        this.deliveryMethods = res.data.deliveryMethods
      })
    },
    getPreferredDeliveryMethod() {
      httpService.get('auth/preferredDeliveryMethod').then(res => {
        this.selectedMethod = res.data.methodId
      })
    },
    getMethodDescription(method) {
      const textArtifacts = _.compact([
        method.descriptionCaption,
        method.descriptionText,
        method.useShippingAgreement ? `${method.price} ${method.currency}` : ''
      ])
      return textArtifacts.join(' ')
    },
    editDeliveryMethod() {
      this.editable = true
      this.oldDelivery = this.selectedMethod
    },
    cancel() {
      this.editable = false
      this.selectedMethod = this.oldDelivery
    },
    save() {
      this.editable = false
      httpService.patch('auth/deliveryMethods', { methodId: this.selectedMethod }).then(() => {
        this.clearCheckout()
      })
    }
  }
}
</script>

<style lang="scss">
.preferred-delivery-methods {
  margin-top: 2.5rem;
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  gap: 0 2rem;
  align-items: flex-start;
}

.preferred-delivery-methods__container {
  max-width: 400px;

  h3 {
    @include h3-semi-bold;
    line-height: 1.5rem;
    margin: 0 0 1.5rem 0;
    color: color(gray-1);
  }
}

.preferred-delivery-methods__accordion {
  margin-bottom: 1.5rem;

  .button {
    align-items: center;
    padding: 0;
    @include body-smaller;
    line-height: 1.3125rem;
    height: auto;
    color: color(gray-1);

    svg {
      order: 1;
      margin-top: 2px;

      &.is-expanded {
        transform: rotate(-90deg);
      }
    }
  }

  .accordion__content {
    max-width: 800px;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-top: 1.5rem;

    h4 {
      margin: 0;
      @include body-smaller-semi-bold;
      color: #000000;
    }

    p {
      margin: 0;
      @include body-smaller;
      color: #000000;
    }
  }
}

.preferred-delivery-methods__radio-buttons {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2.5rem
}

.preferred-delivery-methods__radio-buttons[inert] {
  opacity: 0.5;
}

.preferred-delivery-methods .radio {
  overflow: visible;
}

.preferred-delivery-methods .radio__label {
  padding-left: 50px;
  overflow: visible;

  &::before {
    position: absolute;
    left: 0;
    top: -3px;
    margin: 0;
  }

  &::after {
    top: 12px;
  }
}

.preferred-delivery-methods__bottom {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.preferred-delivery-methods__change-button {
  @include body-semi-bold;
  color: #fff;

  &:disabled {
    display: none;
  }

  svg {
    display: none;
  }
}

@include media(to-small) {
  .preferred-delivery-methods__change-button,
  .preferred-delivery-methods__bottom > .button--blue {
    box-shadow: none;
    background-color: color(brand-blue-3);
  }
}

@include media(small) {
  .preferred-delivery-methods {
    flex-direction: row;
  }

  .preferred-delivery-methods__accordion {
    margin-bottom: 2.5rem;
  }

  .preferred-delivery-methods__radio-buttons {
    margin-bottom: 2rem;
  }

  .preferred-delivery-methods__bottom {
    gap: 1.5rem;
  }

  .preferred-delivery-methods__change-button {
    color: color(brand-blue-3);
    height: auto;

    &:disabled {
      display: flex;
    }

    svg {
      display: inline-block;
    }
  }
}

</style>
